import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  sku: string;
  category: string;
  discount?: number;
  note?: string;
}

const MOCK_CART_ITEMS: CartItem[] = [
  { id: "1", name: "Product A", price: 29.99, quantity: 2, sku: "ELEC-001", category: "Electronics" },
  { id: "2", name: "Product B", price: 15.50, quantity: 1, sku: "CLTH-002", category: "Clothing" },
  { id: "3", name: "Product C", price: 45.00, quantity: 3, sku: "HOME-003", category: "Home" },
];

export default function CartSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [cart, setCart] = useState<CartItem[]>(MOCK_CART_ITEMS);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === id ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const applyItemDiscount = (id: string, discount: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, discount } : item
      )
    );
  };

  const saveNote = (id: string) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, note: noteText } : item
      )
    );
    setEditingNote(null);
    setNoteText("");
  };

  const clearCart = () => {
    setCart([]);
  };

  const subtotal = cart.reduce((acc, item) => {
    const itemTotal = item.price * item.quantity;
    const discount = item.discount ? (itemTotal * item.discount) / 100 : 0;
    return acc + itemTotal - discount;
  }, 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);
  const totalDiscount = cart.reduce((acc, item) => {
    const itemTotal = item.price * item.quantity;
    return acc + (item.discount ? (itemTotal * item.discount) / 100 : 0);
  }, 0);

  return (
    <div className="h-full flex flex-col">
      <div className={`p-4 sm:p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              <i className="ri-shopping-cart-2-line mr-2 text-primary-500"></i>
              {t("posCart.title")}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {totalItems} {t("posTerminal.items")} • {cart.length} {t("posCart.products")}
            </p>
          </div>
          {cart.length > 0 && (
            <button
              onClick={clearCart}
              className="px-4 py-2 rounded-xl border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              {t("posCart.clearCart")}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className={`w-24 h-24 rounded-full ${themeClasses.cardBg} flex items-center justify-center mb-4`}>
              <i className={`ri-shopping-cart-line text-5xl ${themeClasses.textSecondary}`}></i>
            </div>
            <h2 className={`text-xl font-bold ${themeClasses.text} mb-2`}>{t("posCart.emptyTitle")}</h2>
            <p className={themeClasses.textSecondary}>{t("posCart.emptyDescription")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cart.map((item) => (
              <div
                key={item.id}
                className={`${themeClasses.cardBg} rounded-2xl p-4 border ${themeClasses.border}`}
              >
                <div className="flex gap-4">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-box-3-line text-2xl ${themeClasses.textSecondary}`}></i>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className={`font-bold ${themeClasses.text}`}>{item.name}</h3>
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                          {item.sku} • {item.category}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-red-500 hover:text-red-600 p-2"
                      >
                        <i className="ri-close-line text-xl"></i>
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className={`w-8 h-8 rounded-lg ${themeClasses.bg} border ${themeClasses.border} flex items-center justify-center`}
                        >
                          <i className={`ri-subtract-line ${themeClasses.text}`}></i>
                        </button>
                        <span className={`font-bold text-lg ${themeClasses.text} min-w-[2rem] text-center`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="w-8 h-8 rounded-lg bg-primary-500 text-gray-900 flex items-center justify-center"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>

                      <div className="text-right">
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                          ${item.price.toFixed(2)} c/u
                        </p>
                        <p className={`font-bold text-lg ${themeClasses.text}`}>
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                        {item.discount && (
                          <p className="text-sm text-primary-500">
                            -{item.discount}% (-${((item.price * item.quantity * item.discount) / 100).toFixed(2)})
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => applyItemDiscount(item.id, item.discount ? 0 : 10)}
                        className={`px-3 py-1.5 rounded-lg text-sm ${
                          item.discount
                            ? "bg-primary-500 text-gray-900"
                            : `${themeClasses.bg} border ${themeClasses.border} ${themeClasses.text}`
                        }`}
                      >
                        <i className="ri-percent-line mr-1"></i>
                        {item.discount ? `${item.discount}%` : t("posCart.discount")}
                      </button>
                      <button
                        onClick={() => {
                          setEditingNote(item.id);
                          setNoteText(item.note || "");
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm ${themeClasses.bg} border ${themeClasses.border} ${themeClasses.text}`}
                      >
                        <i className="ri-sticky-note-line mr-1"></i>
                        {item.note ? t("posCart.editNote") : t("posCart.addNote")}
                      </button>
                    </div>

                    {editingNote === item.id && (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder={t("posCart.notePlaceholder")}
                          className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
                        />
                        <button
                          onClick={() => saveNote(item.id)}
                          className="px-4 py-2 rounded-lg bg-primary-500 text-gray-900 text-sm font-medium"
                        >
                          {t("posCart.save")}
                        </button>
                      </div>
                    )}

                    {item.note && editingNote !== item.id && (
                      <p className={`mt-2 text-sm ${themeClasses.textSecondary} italic`}>
                        <i className="ri-sticky-note-line mr-1"></i>
                        {item.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {cart.length > 0 && (
        <div className={`p-4 sm:p-6 border-t ${themeClasses.border} ${themeClasses.bg}`}>
          <div className="max-w-md ml-auto space-y-2">
            <div className="flex justify-between">
              <span className={themeClasses.textSecondary}>{t("posTerminal.subtotal")}</span>
              <span className={themeClasses.text}>${(subtotal + totalDiscount).toFixed(2)}</span>
            </div>
            {totalDiscount > 0 && (
              <div className="flex justify-between text-primary-500">
                <span>{t("posCart.totalDiscount")}</span>
                <span>-${totalDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className={themeClasses.textSecondary}>{t("posTerminal.tax")} (16%)</span>
              <span className={themeClasses.text}>${tax.toFixed(2)}</span>
            </div>
            <div className={`flex justify-between text-xl font-bold border-t ${themeClasses.border} pt-3`}>
              <span className={themeClasses.text}>{t("posTerminal.total")}</span>
              <span className="text-primary-500">${total.toFixed(2)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3">
              <button className={`p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium`}>
                <i className="ri-pause-line mr-2"></i>
                {t("posTerminal.hold")}
              </button>
              <button className="p-3 rounded-xl bg-primary-500 text-gray-900 font-bold">
                <i className="ri-wallet-3-line mr-2"></i>
                {t("posTerminal.checkout")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
